import { useEffect, useState } from 'react';
import { Plus, Star, Eye } from 'lucide-react';
import { api, buildQuery } from '../../lib/api';
import type { Property, PropertyListResponse } from '../../types';
import { formatPrice } from '../../lib/format';
import {
  Spinner, EmptyState, StatusBadge, ConfirmDialog,
  secondaryBtn,
} from '../../components/ui';
import PropertyFormModal from '../../components/PropertyFormModal';

export default function Properties() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Property | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get<PropertyListResponse>(
        `/properties${buildQuery({ page, limit: 12, is_active: showInactive ? undefined : '1' })}`
      )
      .then((data) => {
        setProperties(data.items);
        setTotal(data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [page, showInactive]);

  const toggleSold = async (p: Property) => {
    await api.patch(`/properties/${p.id}/sold`, { is_sold: !p.is_sold }).catch(() => {});
    load();
  };

  const toggleFeatured = async (p: Property) => {
    await api.patch(`/properties/${p.id}/feature`, { is_featured: !p.is_featured }).catch(() => {});
    load();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/properties/${deleteId}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold">Properties</h1>
          <p className="text-sm text-gray-500">{total} total</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
              className="mr-2"
            />
            Show inactive
          </label>
          <button onClick={() => { setEditing(null); setModalOpen(true); }} className="inline-flex items-center px-4 py-2 rounded-lg bg-primary text-black text-sm font-medium hover:bg-primary-dark">
            <Plus className="h-4 w-4" /> Add Property
          </button>
        </div>
      </div>

      {loading ? (
        <Spinner label="Loading properties..." />
      ) : properties.length === 0 ? (
        <EmptyState message="No properties found." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Property</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      {p.main_image || p.images?.[0] ? (
                        <img
                          src={p.main_image || p.images[0]}
                          alt={p.title}
                          className="w-14 h-14 rounded-lg object-cover mr-3"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-gray-200 mr-3" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium truncate">{p.title}</p>
                        <p className="text-xs text-gray-500 truncate">{[p.area, p.state].filter(Boolean).join(', ')}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{p.property_code}</td>
                  <td className="px-4 py-3 capitalize">
                    <p>{p.property_type}</p>
                    <p className="text-xs text-gray-500">{p.listing_type}</p>
                  </td>
                  <td className="px-4 py-3">{formatPrice(p.price, p.currency)}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {p.is_sold ? <StatusBadge status="sold" /> : <StatusBadge status="available" />}
                      <div className="text-xs text-gray-500">
                        {p.is_featured && <span className="text-yellow-600 mr-1">Featured</span>}
                        {!p.is_active && <span>Inactive</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <button onClick={() => toggleSold(p)} title={p.is_sold ? 'Mark available' : 'Mark sold'} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button onClick={() => toggleFeatured(p)} title="Toggle featured" className="p-1.5 rounded-lg hover:bg-yellow-50 text-gray-500">
                        <Star className={`h-4 w-4 ${p.is_featured ? 'text-yellow-500 fill-yellow-400' : ''}`} />
                      </button>
                      <button onClick={() => { setEditing(p); setModalOpen(true); }} className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs hover:bg-gray-50">Edit</button>
                      <button onClick={() => setDeleteId(p.id)} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs hover:bg-red-50">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 12 && (
        <div className="flex justify-center mt-6 space-x-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className={secondaryBtn}
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page}</span>
          <button
            disabled={page * 12 >= total}
            onClick={() => setPage(page + 1)}
            className={secondaryBtn}
          >
            Next
          </button>
        </div>
      )}

      <PropertyFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        onSaved={load}
      />

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Property"
        message="Are you sure you want to delete this property? This action cannot be undone."
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
      />
    </div>
  );
}
